/**
 * acknowledgedDays.ts
 *
 * Client-side persistence for the "State C dismissed" acknowledgement.
 * Nothing is sent to the server — this is purely a local UX flag.
 *
 * Storage key format: `daily-rewards:ack:<YYYY-MM-DD>`
 * Value: "1" (truthy string; localStorage only stores strings)
 *
 * Design (S: Single Responsibility) — this module only manages the ack flag.
 * The ViewModel imports these two functions and wires them into sheetState
 * derivation and the acknowledgeDay action.
 */

/** localStorage key prefix (exported so tests can verify the exact key shape). */
export const ACK_KEY_PREFIX = 'daily-rewards:ack:';

/**
 * Returns true when the player has already acknowledged the closed-day
 * "no reward" state for the given day.
 */
export function isDayAcknowledged(day: string): boolean {
  try {
    return localStorage.getItem(`${ACK_KEY_PREFIX}${day}`) === '1';
  } catch {
    // localStorage may be unavailable (e.g. private mode in some browsers)
    return false;
  }
}

/**
 * Persists the acknowledgement flag for the given day to localStorage.
 * Idempotent — calling it multiple times has no additional effect.
 */
export function acknowledgeDay(day: string): void {
  try {
    localStorage.setItem(`${ACK_KEY_PREFIX}${day}`, '1');
  } catch {
    // Silently ignore localStorage errors (quota exceeded, private mode)
  }
}
